import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

interface GenerateSlidesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  onSuccess: () => void;
}

const GenerateSlidesDialog = ({ open, onOpenChange, projectId, onSuccess }: GenerateSlidesDialogProps) => {
  const [minSlides, setMinSlides] = useState(5);
  const [maxSlides, setMaxSlides] = useState(15);
  const [level, setLevel] = useState("intermediate");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (minSlides > maxSlides) {
      toast({
        variant: "destructive",
        title: "Invalid range",
        description: "Minimum slides must be less than or equal to maximum slides",
      });
      return;
    }
    
    setLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('generate-slides', {
        body: { 
          projectId,
          minSlides,
          maxSlides,
          level,
        }
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      toast({
        title: "Success",
        description: "Slide deck generated successfully",
      });
      
      onOpenChange(false);
      onSuccess();
      
    } catch (error) {
      console.error('Generate slides error:', error);
      toast({
        variant: "destructive",
        title: "Error generating slides",
        description: error instanceof Error ? error.message : "Generation failed",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Generate Slide Deck</DialogTitle>
            <DialogDescription>
              Configure your presentation slide deck parameters
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="minSlides">Minimum Slides</Label>
                <Input
                  id="minSlides"
                  type="number"
                  min="1"
                  max="50"
                  value={minSlides}
                  onChange={(e) => setMinSlides(parseInt(e.target.value) || 5)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="maxSlides">Maximum Slides</Label>
                <Input
                  id="maxSlides"
                  type="number"
                  min="1"
                  max="50"
                  value={maxSlides}
                  onChange={(e) => setMaxSlides(parseInt(e.target.value) || 15)}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="level">Difficulty Level</Label>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="intro">Introductory</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Generating..." : "Generate Slides"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default GenerateSlidesDialog;
